<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
      <div class="p-4 border-b border-gray-200">
        <div class="flex justify-between items-center">
          <h2 class="text-xl font-semibold">Keranjang Anda</h2>
          <button @click="close()" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      
      <div class="p-4 max-h-[60vh] overflow-y-auto">
        <div v-if="cart.length === 0" class="text-center py-8">
          <p class="text-gray-500">Keranjang Anda kosong</p>
        </div>
        
        <div v-else class="space-y-4">
          <div v-for="item in cart" :key="item.product.id" class="flex gap-4 py-4 border-b">
            <img
              :src="getItemImage(item.product)"
              :alt="item.product.title"
              @error="handleImageError(item.product)"
              class="w-20 h-20 object-cover rounded"
            />
            <div class="flex-1">
              <h3 class="font-semibold">{{ item.product.title }}</h3>
              <p class="text-sm text-gray-500">stock: {{ item.product.stock }}</p>
              <p class="text-sm text-gray-600">Rp {{ formatPrice((item.product.price).toFixed(2)) }}</p>
              <div class="flex items-center mt-2">
                <div class="flex items-center gap-2">
                  <button
                    @click="updateQuantity(item.product.id, -1)"
                    class="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100"
                    :disabled="item.quantity <= 1"
                  >
                    -
                  </button>
                  <input 
                    type="number" 
                    v-model="item.quantity"
                    @input="handleQuantityInput($event, item)"
                    class="w-12 text-center border rounded-md"
                    :min="1"
                    :max="item.product.stock"
                  />
                  <button 
                    @click="updateQuantity(item.product.id, 1)"
                    class="w-8 h-8 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100"
                    :disabled="item.quantity >= item.product.stock"
                  >
                    +
                  </button>
                </div>
                <button
                  @click="$emit('remove-from-cart', item.product.id)"
                  class="ml-auto text-red-500 hover:text-red-700"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="p-4 border-t border-gray-200">
        <div class="flex justify-between items-center mb-4">
          <span class="font-semibold">Total:</span>
          <span class="font-bold">Rp {{ formatPrice(total) }}</span>
        </div>
        <div class="flex gap-2">
          <button
            @click="showPaymentInfo"
            class="flex-1 bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 disabled:opacity-50"
            :disabled="cart.length === 0"
          >
            Checkout
          </button>
          <button
          @click="close()"
            class="flex-1 bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
    <!-- Payment Info Modal -->
    <PaymentConfirmationModal
  v-if="showPaymentModal"
  :total="total"
  :cart="cart"
  @close="showPaymentModal = false"
  @payment-confirmed="handlePaymentConfirmed"
/>
  </div>
</template>

<script>
import PaymentConfirmationModal from './PaymentConfirmationModal.vue'
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'CartModal',
  components: {
    PaymentConfirmationModal
  },
  props: {
    cart: {
      type: Array,
      required: true
    },
    total: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      showPaymentModal: false
    }
  },
  methods: {
    close() {
    const query = { ...this.$route.query }
    delete query.cart
    this.$router.replace({ query })
  },
    formatPrice(price) {
      // Handle undefined atau null
      if (!price && price !== 0) return '0'
      return new Intl.NumberFormat('id-ID').format(price)
    },
    getItemImage(product) {
      if (product.image?.[0]) {
        return `https://be.ajakstore.com/storage/${product.image[0]}`
      }
      return '/api/placeholder/200/200'
    },
    handleImageError(product) {
      console.error('Image load failed:', {
        imagePath: product.image?.[0],
        fullUrl: this.getItemImage(product)
      })
    },
    showPaymentInfo() {
      this.showPaymentModal = true
    },
    handlePaymentConfirmed() {
      this.$emit('payment-confirmed')
      this.showPaymentModal = false
      this.$emit('close')
    },
    handleQuantityInput(event, item) {
      let value = parseInt(event.target.value)
      if (isNaN(value) || value < 1) {
        item.quantity = 1
      } else if (value > item.product.stock) {
        item.quantity = item.product.stock
      } else {
        item.quantity = value
      }
      this.$emit('update-quantity', item.product.id, 0)
    },
    updateQuantity(productId, change) {
      const item = this.cart.find(item => item.product.id === productId)
      if (!item) return
      
      const newQuantity = item.quantity + change
      if (newQuantity >= 1 && newQuantity <= item.product.stock) {
        this.$emit('update-quantity', productId, change)
      }
    }
  },
  emits: ['close', 'remove-from-cart', 'update-quantity', 'payment-confirmed']
})
</script>